import {Routes} from '@angular/router';
import {MAT_TOOLTIP_DEFAULT_OPTIONS} from '@angular/material/tooltip';
import {provideStates} from '@ngxs/store';
import {HomeComponent} from './home/home.component';
import {PagesComponent} from './pages/pages.component';
import {AuthenticatedGuard} from './auth/guards/authenticated/authenticated.guard';
import {ImagingCenterGuard} from './auth/guards/imaging-center/imaging-center.guard';
import {PublicRequestADemoService} from './public-request-a-demo/services/public-request-a-demo/public-request-a-demo.service';
import {PublicRequestADemoState} from './public-request-a-demo/states/public-request-a-demo/public-request-a-demo.state';
import {SubmissionsService} from './shared/microservices/submission/submissions.service';
import {CustomRegionEditService} from './dashboard/services/custom-region-edit.service';
import {PapayaControlService} from './dashboard/services/papaya-control.service';
import {FactsService} from './dashboard/services/facts.service';
import {MobileScannerWaitListService} from './dashboard/services/mobile-scanner-wait-list.service';
import {AssessmentService} from './dashboard/services/assessment.service';
import {DashboardClinicPatientsService} from './dashboard-clinic-patients/services/dashboard-clinic-patients/dashboard-clinic-patients.service';
import {DashboardClinicPatientsSingleAccountService} from './dashboard-clinic-patients/services/dashboard-clinic-patients-single-account/dashboard-clinic-patients-single-account.service';
import {DashboardSupplementalHealthService} from './dashboard-supplemental-health/services/dashboard-supplemental-health/dashboard-supplemental-health.service';
import {matTooltipDisabledInteractivity} from './shared/utils/mat-tooltip';
import {RtDefineStrokeWidthService} from './rt-define-stroke-width/rt-define-stroke-width.service';
import {DashboardService} from './dashboard-new/services/dashboard/dashboard.service';
import {DashboardGeneticsRetrieveService} from './dashboard-genetics/services/dashboard-genetics-retrieve/dashboard-genetics-retrieve.service';
import {DashboardGeneticsUploadService} from './dashboard-genetics/services/dashboard-genetics-upload/dashboard-genetics-upload.service';
import {DashboardStudyLoadFilesService} from './dashboard-study/services/dashboard-study-load-files/dashboard-study-load-files.service';
import {DashboardStudyPapayaControllerService} from './dashboard-study-papaya/services/dashboard-study-papaya-controller/dashboard-study-papaya-controller.service';
import {DashboardStudyViewerControllerService} from './dashboard-study/services/dashboard-study-viewer-controller/dashboard-study-viewer-controller.service';
import {ScanViewerState} from './dashboard/states/scan-viewer/scan-viewer.state';
import {PrintCardState} from './dashboard/states/print-card/print-card.state';
import {ExploreState} from './dashboard/states/explore/explore.state';
import {DashboardNotificationsState} from './dashboard/states/notifications/notifications.state';
import {ProfileState} from './shared/microservices/profile/profile.state';
import {DashboardClinicPatientsState} from './dashboard-clinic-patients/states/dashboard-clinic-patients/dashboard-clinic-patients.state';
import {DashboardClinicPatientsSingleAccountState} from './dashboard-clinic-patients/states/dashboard-clinic-patients-single-account/dashboard-clinic-patients-single-account.state';
import {DashboardSupplementalHealthState} from './dashboard-supplemental-health/states/dashboard-supplemental-health/dashboard-supplemental-health.state';
import {DashboardViewRegionsState} from './dashboard-view-regions/states/dashboard-view-regions/dashboard-view-regions.state';
import {DashboardState} from './dashboard-new/states/dashboard/dashboard.state';
import {DashboardGeneticsRetrieveState} from './dashboard-genetics/states/dashboard-genetics-retrieve/dashboard-genetics-retrieve.state';
import {DashboardGeneticsUploadState} from './dashboard-genetics/states/dashboard-genetics-upload/dashboard-genetics-upload.state';
import {dashboardRoutes} from './dashboard-new/dashboard.routes';
import {environment} from '../environments/environment';
import {EnvironmentName} from '../environments/symbols';

export const appRoutes = (): Routes => {
  const stableRoutes: Routes = [
    {
      path: '',
      component: HomeComponent,
      data: {
        title: 'Turn Brain Scans into Actionable Insights',
        meta: [
          {
            name: 'description',
            content:
              'BrainKey is a personal 3D brain dashboard built by neuroscientists with the goal of empowering individuals and researchers to better understand the brain and improve brain health.',
          },
        ],
      },
    },
    {
      path: '',
      component: PagesComponent,
      children: [
        {
          path: 'about',
          loadChildren: () => import('./pages/about/about.module').then(m => m.AboutModule),
        },
        {
          path: 'cookies',
          loadChildren: () => import('./pages/cookies/cookies.module').then(m => m.CookiesModule),
        },
        {
          path: 'privacy',
          loadChildren: () => import('./pages/privacy/privacy.module').then(m => m.PrivacyModule),
        },
        {
          path: 'account-deletion',
          loadChildren: () => import('./pages/account-deletion/account-deletion.module').then(m => m.AccountDeletionModule),
        },
        {
          path: 'terms-of-service',
          loadChildren: () => import('./pages/terms-of-service/terms-of-service.module').then(m => m.TermsOfServiceModule),
        },
        {
          path: 'contact',
          loadChildren: () => import('./pages/contact/contact.module').then(m => m.ContactModule),
        },
        {
          path: 'faq',
          loadChildren: () => import('./pages/faq/faq.module').then(m => m.FaqModule),
        },
        {
          path: 'publications',
          loadChildren: () => import('./pages/publications/publications.module').then(m => m.PublicationsModule),
        },
        {
          path: 'get-started',
          loadChildren: () => import('./pages/get-started/get-started.module').then(m => m.GetStartedModule),
        },
        {
          path: 'collaborators',
          loadChildren: () => import('./pages/collaborators/collaborators.module').then(m => m.CollaboratorsModule),
        },
        {
          path: 'video',
          loadChildren: () => import('./pages/brainkey-video/brainkey-video.module').then(m => m.BrainkeyVideoModule),
        },
        {
          path: 'physician-faq',
          loadChildren: () => import('./pages/physician-faq/physician-faq.module').then(m => m.PhysicianFaqModule),
        },
        {
          path: 'scan-quality',
          loadChildren: () => import('./pages/scan-quality/scan-quality.module').then(m => m.ScanQualityModule),
        },
        {
          path: 'locations',
          loadChildren: () => import('./public-locations/public-locations.module').then(m => m.PublicLocationsModule),
        },
        {
          path: 'access-private-ar',
          loadChildren: () => import('./pages/access-private-ar/access-private-ar.module').then(m => m.AccessPrivateArModule),
        },
        {
          path: 'request-demo',
          loadComponent: () =>
            import('./public-request-a-demo/components/public-request-a-demo/public-request-a-demo.component').then(
              m => m.PublicRequestADemoComponent
            ),
          providers: [PublicRequestADemoService, provideStates([PublicRequestADemoState])],
        },
      ],
    },
    {
      path: 'demo',
      loadChildren: () => import('./old-modules/demo/demo.module').then(m => m.DemoModule),
      canActivate: [],
    },
    {
      path: 'settings',
      loadChildren: () => import('./old-modules/settings/settings.module').then(m => m.SettingsModule),
      canActivate: [AuthenticatedGuard],
    },
    {
      path: 'auth',
      loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule),
    },
    {
      path: 'blog',
      loadChildren: () => import('./pages/blog/blog.module').then(m => m.BlogModule),
    },
    {
      path: 'mri-scheduling',
      redirectTo: '',
      pathMatch: 'full',
    },
    {
      path: 'mri-scheduling/welcome',
      redirectTo: '',
      pathMatch: 'full',
    },
    {
      path: 'ar-demo',
      loadChildren: () => import('./pages/ar-demo/ar-demo.module').then(m => m.ArDemoModule),
    },
    {
      path: 'ar-experience',
      loadChildren: () => import('./pages/ar-experience/ar-experience.module').then(m => m.ArExperienceModule),
    },
    {
      path: 'print-your-brain',
      loadChildren: () => import('./pages/print-your-brain/print-your-brain.module').then(m => m.PrintYourBrainModule),
    },
    {
      path: 'scan-types',
      loadChildren: () => import('./pages/scan-types/scan-types.module').then(m => m.ScanTypesModule),
    },
    {
      path: 'imaging-center',
      loadChildren: () => import('./old-imaging-center/imaging-center.module').then(m => m.ImagingCenterModule),
      canActivate: [AuthenticatedGuard, ImagingCenterGuard],
    },
    {
      path: 'scan-policies',
      loadChildren: () => import('./pages/scan-policies/scan-policies.module').then(m => m.ScanPoliciesModule),
    },
    {
      path: 'retrieve-mri',
      pathMatch: 'full',
      redirectTo: '',
    },
    {
      path: 'mri-records',
      loadChildren: () => import('./old-modules/mri-records/mri-records.module').then(m => m.MriRecordsModule),
    },
    {
      path: 'mri',
      loadChildren: () => import('./pages/learn-more/learn-more.module').then(m => m.LearnMoreModule),
    },
    {
      path: 'MRI',
      redirectTo: '/mri',
      pathMatch: 'full',
    },
    {
      path: 'backbayimaging',
      loadChildren: () => import('./pages/learn-more/learn-more.module').then(m => m.LearnMoreModule),
    },
    {
      path: 'westernimaging',
      loadChildren: () => import('./pages/learn-more/learn-more.module').then(m => m.LearnMoreModule),
    },
    {
      path: 'simonmed',
      loadChildren: () => import('./pages/simon-med/simon-med.module').then(m => m.SimonMedModule),
    },
    {
      path: 'physicians',
      loadChildren: () => import('./pages/for-physicians/for-physicians.module').then(m => m.ForPhysiciansModule),
    },
    {
      path: 'demo-viewer/:id',
      loadChildren: () => import('./pages/demo-viewer/demo-viewer.module').then(m => m.DemoViewerModule),
    },
    // Temporarily redirecting to the tutorial page as the How It Works page is under the rework.
    // See: https://github.com/BrainKey/brainkey-api/issues/4088
    {
      path: 'how-it-works',
      redirectTo: 'tutorial',
      pathMatch: 'full',
    },
    {
      path: 'tutorial',
      loadChildren: () => import('./pages/brainkey-tutorial/brainkey-tutorial.module').then(m => m.BrainkeyTutorialModule),
    },
    {
      path: 'centrelake',
      loadChildren: () => import('./pages/centre-lake/centre-lake.module').then(m => m.CentreLakeModule),
    },
    {
      path: 'patient-records-upload/:mriRecordsRequestId',
      loadChildren: () => import('./old-modules/mri-records-upload/mri-records-upload.module').then(m => m.MriRecordsUploadModule),
    },
    {
      path: 'dashboard',
      loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    },
    // Disabled.
    // Request on Figma:
    // https://www.figma.com/design/SJ5GI2vJKY9wnSWIoG06TV/Brainkey_Style-Guide---Primary?node-id=23937-1713&node-type=FRAME&t=ZmQybLnfrDaf6Dji-0#930649483
    {
      path: 'schedule-mri',
      // loadChildren: () => import('./pages/schedule-mri/schedule-mri.module').then(m => m.ScheduleMriModule),
      redirectTo: '',
    },
  ];

  const notFoundRoutes: Routes = [
    {
      path: '**',
      loadChildren: () => import('./pages/not-found/not-found.module').then(m => m.NotFoundModule),
    },
  ];

  const prototypeRoutes: Routes = [
    {
      path: 'dashboard-new',
      loadComponent: () =>
        import('./dashboard-new/components/dashboard-new-root/dashboard-new-root.component').then(m => m.DashboardNewRootComponent),
      providers: [
        SubmissionsService,
        CustomRegionEditService,
        PapayaControlService,
        FactsService,
        MobileScannerWaitListService,
        AssessmentService,
        DashboardClinicPatientsService,
        DashboardClinicPatientsSingleAccountService,
        DashboardSupplementalHealthService,
        {provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: matTooltipDisabledInteractivity},
        RtDefineStrokeWidthService,
        DashboardService,
        DashboardGeneticsRetrieveService,
        DashboardGeneticsUploadService,
        DashboardStudyLoadFilesService,
        DashboardStudyPapayaControllerService,
        DashboardStudyViewerControllerService,
        provideStates([
          ScanViewerState,
          PrintCardState,
          ExploreState,
          DashboardNotificationsState,
          ProfileState,
          DashboardClinicPatientsState,
          DashboardClinicPatientsSingleAccountState,
          DashboardSupplementalHealthState,
          DashboardViewRegionsState,
          DashboardState,
          DashboardGeneticsRetrieveState,
          DashboardGeneticsUploadState,
        ]),
      ],
      children: dashboardRoutes,
    },
  ];

  // Keep the new prototype available only on the development environment.
  return environment.name === EnvironmentName.Local || environment.name === EnvironmentName.Dev
    ? [...stableRoutes, ...prototypeRoutes, ...notFoundRoutes]
    : [...stableRoutes, ...notFoundRoutes];
};
